import { getApiUrl } from "@/utils/api";
import { useState, useEffect } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Monitor, Smartphone, Tablet, Loader2 } from "lucide-react";
import axios from "axios";

const API = getApiUrl();

const DEVICE_COLORS = {
  desktop: "#2f4f4f",
  mobile: "#e07b5d",
  tablet: "#3b82f6",
};

const BROWSER_COLORS = ["#2f4f4f", "#e07b5d", "#3b82f6", "#8b5cf6", "#f59e0b", "#10b981", "#ec4899", "#6b7280"];
const OS_COLORS = ["#1e3a3a", "#c4634a", "#2563eb", "#7c3aed", "#d97706", "#059669", "#db2777", "#4b5563"];

const DEVICE_ICONS = { desktop: Monitor, mobile: Smartphone, tablet: Tablet };

function DonutChart({ data, colors, title }) {
  const total = data.reduce((s, d) => s + d.count, 0);
  return (
    <div className="bg-white border border-border rounded-lg p-5">
      <h3 className="font-serif text-sm font-medium mb-3">{title}</h3>
      {data.length > 0 ? (
        <>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={data} dataKey="count" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={2}>
                {data.map((_, i) => (
                  <Cell key={i} fill={colors[i % colors.length]} />
                ))}
              </Pie>
              <Tooltip
                content={({ active, payload }) =>
                  active && payload?.length ? (
                    <div className="bg-white border border-border shadow-md p-2 rounded text-xs">
                      <p className="font-medium">{payload[0].name}</p>
                      <p>{payload[0].value.toLocaleString()} ({Math.round(payload[0].value / total * 100)}%)</p>
                    </div>
                  ) : null
                }
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5 mt-2">
            {data.map((item, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: colors[i % colors.length] }} />
                  <span className="capitalize">{item.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{item.count.toLocaleString()}</span>
                  <span className="text-muted-foreground w-8 text-right">{Math.round(item.count / total * 100)}%</span>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <p className="text-sm text-muted-foreground text-center py-8">No data yet</p>
      )}
    </div>
  );
}

export default function AnalyticsDevices({ period }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/analytics/devices?period=${period}`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [period]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
      </div>
    );
  }

  const deviceData = (data?.devices || []).map((d) => ({ ...d, name: d.name }));
  const total = deviceData.reduce((s, d) => s + d.count, 0);

  return (
    <div className="space-y-5" data-testid="analytics-devices">
      {/* Device type hero cards */}
      <div className="grid grid-cols-3 gap-3">
        {["desktop", "mobile", "tablet"].map((type) => {
          const item = deviceData.find((d) => d.name.toLowerCase() === type);
          const count = item?.count || 0;
          const pct = total > 0 ? Math.round(count / total * 100) : 0;
          const Icon = DEVICE_ICONS[type];
          return (
            <div key={type} className="bg-white border border-border rounded-lg p-4 text-center">
              <Icon className="h-6 w-6 mx-auto mb-2 text-primary" />
              <p className="text-2xl font-bold font-serif">{pct}%</p>
              <p className="text-xs text-muted-foreground capitalize mt-0.5">{type}</p>
              <p className="text-xs text-muted-foreground">{count.toLocaleString()} sessions</p>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <DonutChart
          data={deviceData}
          colors={deviceData.map((d) => DEVICE_COLORS[d.name.toLowerCase()] || "#6b7280")}
          title="Device Type"
        />
        <DonutChart
          data={data?.browsers || []}
          colors={BROWSER_COLORS}
          title="Browser"
        />
        <DonutChart
          data={data?.os || []}
          colors={OS_COLORS}
          title="Operating System"
        />
      </div>

      {/* Summary table */}
      {(data?.browsers?.length > 0 || data?.os?.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white border border-border rounded-lg p-5">
            <h3 className="font-serif text-sm font-medium mb-3">Browser Breakdown</h3>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-1.5 text-muted-foreground font-normal">Browser</th>
                  <th className="text-right py-1.5 text-muted-foreground font-normal">Sessions</th>
                  <th className="text-right py-1.5 text-muted-foreground font-normal">Share</th>
                </tr>
              </thead>
              <tbody>
                {(data?.browsers || []).map((b, i) => (
                  <tr key={i} className="border-b border-border last:border-0">
                    <td className="py-2">{b.name}</td>
                    <td className="text-right py-2 font-medium">{b.count.toLocaleString()}</td>
                    <td className="text-right py-2 text-muted-foreground">{Math.round(b.count / total * 100)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="bg-white border border-border rounded-lg p-5">
            <h3 className="font-serif text-sm font-medium mb-3">OS Breakdown</h3>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-1.5 text-muted-foreground font-normal">OS</th>
                  <th className="text-right py-1.5 text-muted-foreground font-normal">Sessions</th>
                  <th className="text-right py-1.5 text-muted-foreground font-normal">Share</th>
                </tr>
              </thead>
              <tbody>
                {(data?.os || []).map((o, i) => (
                  <tr key={i} className="border-b border-border last:border-0">
                    <td className="py-2">{o.name}</td>
                    <td className="text-right py-2 font-medium">{o.count.toLocaleString()}</td>
                    <td className="text-right py-2 text-muted-foreground">{Math.round(o.count / total * 100)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
